import PropTypes from "prop-types"
import React, { useCallback, useEffect, useRef } from "react"
import { useForm } from "react-hook-form"
import { useMutation, useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Button from "src/components/Button"
import DiscardConfirmModal from "src/components/DiscardConfirmModal"
import Modal from "src/components/Modal"

import { updateJob } from "src/api/Service/Jobs"

import { useDiscardConfirm } from "src/hooks/use_discard_confirm"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import JobFormFields from "./JobFormFields"

const EditJobModal = ({ isOpen, onClose, job, workOrderSequentialId }) => {
  const { marinaSlug } = useMarina()
  const showToast = useToast()
  const queryClient = useQueryClient()
  const tracker = useTracker()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      name: job.name,
      description: job.description ?? "",
    },
  })

  const wasOpenRef = useRef(isOpen)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      reset({ name: job.name, description: job.description ?? "" })
    }
    wasOpenRef.current = isOpen
  }, [isOpen, job, reset])

  const { handleCloseRequest, discardConfirmProps } = useDiscardConfirm({
    isDirty,
    onClose,
  })

  const handleDiscard = useCallback(() => {
    tracker.trackEvent("work_orders:edit_job:discarded", { jobId: job.id })
    discardConfirmProps.onDiscard()
  }, [tracker, job.id, discardConfirmProps])

  const { mutate: submit, isLoading } = useMutation(
    (values) => updateJob(marinaSlug, workOrderSequentialId, job.id, values),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "workOrder",
          marinaSlug,
          workOrderSequentialId,
        ])
        queryClient.invalidateQueries(["jobActivity", marinaSlug, job.id])
        showToast("Job updated", { type: "success" })
        onClose()
      },
      onError: (error) => {
        showToast(error.message || "Failed to update job.", { type: "error" })
      },
    }
  )

  const onSubmit = (values) =>
    submit({ name: values.name, description: values.description })

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleCloseRequest} size="default">
        <Modal.Header title="Edit Job" />
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <JobFormFields control={control} errors={errors} />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              type="button"
              onClick={handleCloseRequest}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleSubmit(onSubmit)}
              isLoading={isLoading}
              disabled={!isDirty || isLoading}
            >
              Save
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <DiscardConfirmModal {...discardConfirmProps} onDiscard={handleDiscard} />
    </>
  )
}

EditJobModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  job: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
  }).isRequired,
  workOrderSequentialId: PropTypes.string.isRequired,
}

export default EditJobModal
